import { useMutation, useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { PostCard } from "../features/display/PostCard.tsx";
import {
  advance,
  type FeedPost,
  initialRotation,
  mergeFeed,
  type RotationState,
} from "../features/display/rotation.ts";
import { api } from "../lib/api.ts";

const FEED_POLL_MS = 3_000;
const SLOT_MS = 10_000;

export const Route = createFileRoute("/s/$token")({
  component: ScreenDisplay,
});

function ScreenDisplay() {
  const { token } = Route.useParams();
  const [rotation, setRotation] = useState<RotationState>(initialRotation);
  const rotationRef = useRef(rotation);
  rotationRef.current = rotation;

  const feed = useQuery({
    queryKey: ["screen-feed", token],
    queryFn: async () => {
      const { data, error } = await api.GET("/api/v1/screen/feed", {
        params: { header: { "x-screen-token": token } },
      });
      if (error) throw new Error("screen feed unavailable");
      return data?.data as unknown as { pending: FeedPost[]; recent: FeedPost[] };
    },
    refetchInterval: FEED_POLL_MS,
  });

  const ack = useMutation({
    mutationFn: async (postId: string) => {
      await api.POST("/api/v1/screen/feed/ack", {
        params: { header: { "x-screen-token": token } },
        body: { postIds: [postId] },
      });
    },
  });

  // Fold each poll into the rotation state.
  const feedData = feed.data;
  useEffect(() => {
    if (!feedData) return;
    setRotation((state) => mergeFeed(state, feedData.pending ?? [], feedData.recent ?? []));
  }, [feedData]);

  // Advance the display slot on a fixed cadence; ack finished live posts.
  const ackMutate = ack.mutate;
  useEffect(() => {
    const tick = () => {
      const result = advance(rotationRef.current);
      setRotation(result.state);
      if (result.ackId) ackMutate(result.ackId);
    };

    // First paint should not wait a full slot.
    tick();
    const interval = setInterval(tick, SLOT_MS);
    return () => clearInterval(interval);
  }, [ackMutate]);

  const joinUrl = typeof window === "undefined" ? "" : window.location.origin;

  return (
    <main className="relative h-screen w-screen overflow-hidden bg-[#0e0d0b]">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(255,255,255,0.06),transparent_60%)]" />

      {rotation.current ? (
        <PostCard post={rotation.current} isLive={rotation.isLive} />
      ) : (
        <IdleScreen unauthorized={feed.isError} />
      )}

      <footer className="absolute bottom-6 left-8 right-8 flex items-end justify-between text-white/50">
        <p className="font-display text-2xl">
          วันนี้เบาๆนะ <span className="text-white/30">· Just One Drink</span>
        </p>
        <p className="text-xl">สแกน QR ที่โต๊ะ แล้วส่งข้อความขึ้นจอ 🍸{joinUrl ? "" : ""}</p>
      </footer>
    </main>
  );
}

function IdleScreen({ unauthorized }: { unauthorized: boolean }) {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-6 text-center text-white">
      <h1 className="font-display text-7xl font-bold">คืนนี้อยากบอกอะไรใคร? 💬</h1>
      <p className="max-w-3xl text-3xl text-white/60">
        สแกน QR ที่โต๊ะ → เลือก “ขึ้นจอ” → ส่งข้อความ แจกวาร์ป ขอเพลง หรือแจกทิปให้วง
      </p>
      {unauthorized ? (
        <p className="text-2xl text-red-300/80">จอนี้เชื่อมต่อไม่สำเร็จ — ตรวจสอบลิงก์จอใน Owner Admin</p>
      ) : null}
    </div>
  );
}
